<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText
        v-if="fields?.title?.value"
        tag="h2"
        :class="!fields?.form ? 'faq-title' : ''"
        :field="fields?.title"
      />
      <div class="faq-form" v-if="fields?.form">
        <DynamicForm :form="fields?.form" @iconClick="handleChange" />
      </div>
      <div class="faq-items">
        <div class="faq-filter" v-if="fields?.filterList?.length > 0">
          <div class="faq-filter-header" @click="toggleFilter">
            <JssRichText class="faq-filter-title" :field="fields?.filterTitle" />
            <Icon
              :svg="fields?.downIcon?.value?.svgCode"
              class="faq-filter-icon"
              :class="{ rotated: state.filterExpanded }"
            />
          </div>
          <div class="faq-filter-content" v-if="state.filterExpanded && filterList.length > 0">
            <div
              v-for="(item, index) in filterList"
              :key="index"
              class="faq-filter-item"
              :class="{
                active:
                  item.key === 'all' ? isAllSelected : state.selectedFilters.includes(item.key),
              }"
              @click="selectFilter(item?.key)"
            >
              <div class="faq-filter-item-checkbox">
                <Icon
                  name="check"
                  class="faq-filter-item-check"
                  :class="{
                    visible:
                      item.key === 'all'
                        ? isAllSelected
                        : state.selectedFilters.includes(item?.key),
                  }"
                />
              </div>
              <span class="faq-filter-item-label">{{ item?.label }}</span>
            </div>
          </div>
        </div>
        <div class="faq-item-list" v-if="paginatedList.length > 0">
          <div
            v-for="(item, index) in paginatedList"
            :key="index"
            class="faq-item"
            :class="{ active: state.expandedIndexes.includes(getOriginalIndex(index)) }"
          >
            <div class="faq-item-header" @click="toggleExpand(getOriginalIndex(index))">
              <div class="faq-item-icon-wrapper">
                <Icon
                  v-if="state.expandedIndexes.includes(getOriginalIndex(index))"
                  :svg="fields?.activeIcon?.value?.svgCode"
                  class="faq-item-icon active"
                />
                <Icon v-else :svg="fields?.icon?.value?.svgCode" class="faq-item-icon" />
              </div>
              <span class="faq-item-number">{{ getOriginalIndex(index) + 1 }}.</span>
              <JssRichText class="faq-item-question" :field="item.fields.question" />
              <Icon
                :svg="fields?.downIcon?.value?.svgCode"
                class="faq-item-down-icon"
                :class="{ rotated: state.expandedIndexes.includes(getOriginalIndex(index)) }"
              />
            </div>
            <div class="faq-item-answer">
              <JssRichText class="faq-item-answer-inner" :field="item?.fields?.answer" />
            </div>
          </div>
        </div>
      </div>
      <Pagination
        v-model:currentPage="currentPage"
        :totalPages="totalPages"
        v-if="totalPages > 1"
      />
    </ContentGrid>
  </div>
</template>

<script>
import { reactive, computed, ref, onMounted, watch } from 'vue';
import utils from '@/utils';
import api from '@/api';

export default {
  name: 'Faq',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const currentPage = ref(1);
    const keyword = ref('');
    const totalCount = ref(0);
    const faqList = ref([]);
    const state = reactive({
      expandedIndexes: [],
      filterExpanded: true,
      selectedFilters: [],
    });

    const itemsPerPage = computed(() => {
      const pageSize = props.fields?.pageSize?.value;
      return pageSize ? Number(pageSize) : 5;
    });

    const filterList = computed(() => {
      const list = props.fields?.filterList || [];
      return [
        { key: 'all', label: 'All' },
        ...list.map((item) => ({
          key: item.fields?.phrase?.value,
          label: item.fields?.text?.value || item.fields?.label?.value || '',
        })),
      ];
    });

    const isAllSelected = computed(() => {
      const allFilterKeys = filterList.value.slice(1).map((item) => item.key);
      return allFilterKeys.every((key) => state.selectedFilters.includes(key));
    });

    const tagParam = computed(() => {
      const allFilterKeys = filterList.value.slice(1).map((item) => item.key);
      if (state.selectedFilters.length === 0) return '';
      if (state.selectedFilters.length === allFilterKeys.length) return '';
      return state.selectedFilters.join(',');
    });

    const totalPages = computed(() => {
      return Math.max(1, Math.ceil(totalCount.value / itemsPerPage.value));
    });

    const paginatedList = computed(() => faqList.value);

    const getOriginalIndex = (paginatedIndex) => {
      return (currentPage.value - 1) * itemsPerPage.value + paginatedIndex;
    };

    const loadFaqs = async () => {
      const [res, ex] = await api.faq.getFaq({
        id: props?.rendering?.dataSource,
        page: currentPage.value,
        size: itemsPerPage.value,
        keyword: keyword.value,
        tag: tagParam.value,
        lang: props.page.itemLanguage,
      });
      const data = res || ex;
      faqList.value = data?.list || [];
      totalCount.value = data?.total || 0;
    };

    const methods = {
      toggleExpand(index) {
        const indexInArray = state.expandedIndexes.indexOf(index);
        if (indexInArray > -1) {
          state.expandedIndexes.splice(indexInArray, 1);
        } else {
          state.expandedIndexes.push(index);
        }
      },
      toggleFilter() {
        state.filterExpanded = !state.filterExpanded;
      },
      selectFilter(key) {
        const allFilterKeys = filterList.value.slice(1).map((item) => item.key);
        if (key === 'all') {
          if (isAllSelected.value) {
            state.selectedFilters = [];
          } else {
            state.selectedFilters = [...allFilterKeys];
          }
        } else {
          const index = state.selectedFilters.indexOf(key);
          if (index > -1) {
            state.selectedFilters.splice(index, 1);
          } else {
            state.selectedFilters.push(key);
          }
        }
        currentPage.value = 1;
      },
    };

    const isEmptyContent = computed(() => {
      return faqList.value.length === 0;
    });

    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'faq', {
        'media-mode': isEmptyContent.value,
      })
    );

    const rootStyle = computed(() => {
      return utils.overall.baseComStyle(props.fields);
    });

    const handleChange = (fieldName, value) => {
      keyword.value = value || '';
      currentPage.value = 1;
    };

    const initFilters = () => {
      const allFilterKeys = filterList.value.slice(1).map((item) => item.key);
      state.selectedFilters = [...allFilterKeys];
    };

    onMounted(() => {
      initFilters();
      loadFaqs();
    });

    watch(
      () => filterList.value,
      () => {
        initFilters();
      },
      { deep: true }
    );

    watch([currentPage, keyword, tagParam], () => {
      loadFaqs();
    });

    return {
      currentPage,
      totalPages,
      paginatedList,
      handleChange,
      getOriginalIndex,
      filterList,
      isAllSelected,
      state,
      ...methods,
      rootClass,
      rootStyle,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.faq {
  position: relative;
  width: 100%;
  padding: 40px 0;
  font-family: 'Albert Sans', sans-serif !important;

  &-form {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 24px;

    .c-dynamic-form__items {
      justify-content: flex-end;

      .f-input {
        gap: 0 !important;
      }
    }

    .c-dynamic-form__items {
      width: 100%;

      .f-input__main {
        height: 40px;

        svg {
          width: 16px;
          height: 16px;
          position: relative;
          top: -3px;
        }
      }
    }
  }

  &-title {
    text-align: center;
    margin-bottom: 24px;
  }

  &-filter {
    z-index: 10;
    margin-bottom: 24px;
    overflow: hidden;

    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      background: #f8f8f8;
      cursor: pointer;

      &:hover {
        background: #f0f0f0;
      }
    }

    &-title {
      font-size: 16px;
      font-weight: 600;
      color: $black;
    }

    &-icon {
      width: 16px;
      height: 16px;
      transition: transform 0.3s ease;
      fill: transparent !important;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &-content {
      padding: 8px 0;
    }

    &-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: #f8f8f8;
      }

      &.active {
        .faq-filter-item-checkbox {
          background: #739b78;
          border-color: #739b78;
        }
      }
    }

    &-item-checkbox {
      width: 18px;
      height: 18px;
      border: 2px solid #c4c4c4;
      border-radius: 3px;
      margin-right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    &-item-check {
      width: 12px;
      height: 12px;
      color: $white;
      opacity: 0;
      transition: opacity 0.2s ease;

      &.visible {
        opacity: 1;
      }
    }

    &-item-label {
      flex: 1;
      font-size: 14px;
      color: $black;
      word-break: break-word;
      white-space: normal;
    }

    &-item-count {
      font-size: 14px;
      color: #656565;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }

  &-item {
    border-bottom: 1px solid #e5e5e5;
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      .faq-item-icon {
        opacity: 0;
      }
      .faq-item-icon.active {
        opacity: 1;
      }
    }

    &-header {
      display: flex;
      align-items: center;
      padding: 16px 0;
      position: relative;
    }

    &-icon-wrapper {
      position: relative;
      width: 32px;
      height: 32px;
      margin-right: 12px;
    }

    &-icon {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: opacity 0.3s ease;
      fill: transparent !important;

      &.active {
        opacity: 0;
      }
    }

    &-number {
      flex-shrink: 0;
      font-size: 14px;
      font-weight: 600;
      color: $black;
      margin-right: 8px;
    }

    &-question {
      flex: 1;
      font-size: 14px;
      font-weight: 600;
      color: $black;
      line-height: 1.5;
    }

    &-down-icon {
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 12px;
      transition: transform 0.3s ease;
      fill: transparent !important;
      transform-origin: center center;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &-answer {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.4s ease-in-out;
      overflow-x: auto;
      &::-webkit-scrollbar {
        display: none;
      }

      .active & {
        max-height: 10000px;
      }

      &-inner {
        padding: 0 0 16px 36px;
        font-size: 13px;
        color: #404040;
        line-height: 1.6;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-title {
      margin-bottom: 40px;
    }

    &-items {
      display: flex;
      gap: 24px;
    }

    &-filter {
      width: 250px;
      flex-shrink: 0;
      flex-grow: 0;
    }

    &-item {
      border-bottom: 1px solid #e5e5e5;
      cursor: pointer;
      transition: all 0.3s ease;
      &-list {
        flex: 1;
      }

      &.active {
        .faq-item-icon {
          opacity: 0;
        }
        .faq-item-icon.active {
          opacity: 1;
        }
      }

      &-header {
        display: flex;
        align-items: center;
        padding: 24px 0;
        position: relative;
      }

      &-icon-wrapper {
        position: relative;
        width: 32px;
        height: 32px;
        margin-right: 16px;
      }

      &-icon {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: opacity 0.3s ease;
        fill: transparent;

        &.active {
          opacity: 0;
        }
      }

      &-number {
        flex-shrink: 0;
        font-size: 16px;
        font-weight: 700;
        color: $black;
        margin-right: 12px;
      }

      &-question {
        flex: 1;
        font-size: 16px;
        font-weight: 700;
        color: $black;
        line-height: 1.5;
      }

      &-down-icon {
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: 16px;
        transition: transform 0.3s ease;
        fill: transparent !important;
        transform-origin: center center;

        &.rotated {
          transform: rotate(180deg);
        }
      }

      &-answer {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.4s ease-in-out;

        .active & {
          max-height: 10000px;
        }

        &-inner {
          padding: 0 0 24px 48px;
          font-size: 14px;
          color: #404040;
          line-height: 1.8;

          p {
            line-height: 1.8;
          }
        }
      }
    }
  }
}
</style>
